import { DialogWrapper } from '@/components/common/DialogWrapper';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import type { CartItem, CartPriceBreakdown } from '@/domain/Cart';
import { CartPeriod } from '@/domain/Cart';
import { cn } from '@/lib/cn';

interface CartPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: Array<CartItem>;
  period?: number;
  priceBreakdown?: CartPriceBreakdown;
}

export function CartPreviewDialog({
  open,
  onOpenChange,
  items,
  period = CartPeriod.MONTHLY,
  priceBreakdown,
}: CartPreviewDialogProps) {
  const total = priceBreakdown?.total ?? 0;
  const subtotal = priceBreakdown?.subtotal ?? 0;
  const monthlyPrice = total / period;
  const subMonthlyPrice = subtotal / period;
  const hasItems = total > 0;
  const hasPeriodDiscount =
    hasItems &&
    period !== CartPeriod.MONTHLY &&
    (priceBreakdown?.period_discount_percent ?? 0) > 0;
  const hasPromoDiscount =
    hasItems && (priceBreakdown?.promo_discount_percent ?? 0) > 0;
  const totalBeforePromo = total + (priceBreakdown?.promo_discount ?? 0);

  const Row = ({
    label,
    value,
    previous,
    discount,
  }: {
    label: string;
    value: string;
    previous?: string;
    discount?: number;
  }) => (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-medium">{label}</p>
      <div className="flex items-center gap-3">
        {discount ? (
          <p className="bg-primary text-primary-foreground flex h-7.5 items-center justify-center rounded-full px-3 text-xs">{`Save ${discount} %`}</p>
        ) : null}
        {previous ? (
          <p className="text-muted-foreground line-through">{previous}</p>
        ) : null}
        <p>{value}</p>
      </div>
    </div>
  );

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title="Cart Preview"
      maxWidth="md"
    >
      <div className="flex flex-col gap-6">
        <Table className="mt-2 [&_td]:whitespace-normal [&_th]:whitespace-normal">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Supplement</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Quantity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  No items in cart.
                </TableCell>
              </TableRow>
            ) : (
              items.map((cartItem) => (
                <TableRow key={cartItem.id}>
                  <TableCell className="py-1">
                    {cartItem.item_price.item?.name || 'Supplement'} |{' '}
                    {cartItem.item_price.strength}
                  </TableCell>
                  <TableCell className="py-1">
                    €{cartItem.item_price.formatted_price}
                  </TableCell>
                  <TableCell className="py-1">{cartItem.quantity}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div
          className={cn(
            'border-border flex flex-col gap-3 border-t pt-4',
            !hasItems && 'text-muted-foreground',
          )}
        >
          <Row
            label="Monthly cost"
            discount={
              hasPeriodDiscount
                ? priceBreakdown?.period_discount_percent
                : undefined
            }
            previous={
              hasPeriodDiscount
                ? `€${(subMonthlyPrice / 100).toFixed(2)}`
                : undefined
            }
            value={`€${(monthlyPrice / 100).toFixed(2)}`}
          />
          <Row
            label="Subscription cost"
            previous={
              hasPeriodDiscount ? `€${(subtotal / 100).toFixed(2)}` : undefined
            }
            value={`€${(total / 100).toFixed(2)}`}
          />
          {hasPromoDiscount ? (
            <Row
              label="Subscription cost with additional discount"
              discount={priceBreakdown?.promo_discount_percent}
              previous={`€${(totalBeforePromo / 100).toFixed(2)}`}
              value={`€${(total / 100).toFixed(2)}`}
            />
          ) : null}
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium">Subscription period</p>
            <p>{period} month(s)</p>
          </div>
        </div>
      </div>
    </DialogWrapper>
  );
}
